'use client';
import { useTheme } from '@/hooks/use-theme';
import semaLogo from '@/public/sema-logo.png';
import {
  Bell,
  Brain,
  FileText,
  GitCompare,
  House,
  LogOut,
  Menu,
  Moon,
  Settings,
  Sun,
  TreePine,
  User,
} from 'lucide-react';
import Image from 'next/image';
import { ReactNode, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { NavButton } from './components/NavButton';

type NavType =
  | 'dashboard'
  | 'comparision'
  | 'predictive'
  | 'reports'
  | 'settings';

interface Nav {
  id: NavType;
  icon: ReactNode;
  label: string;
}

const Sidebar = () => {
  const { theme, handleTheme } = useTheme();
  const [tabSelected, setTabSelected] = useState<NavType>('dashboard');
  const [contracted, setContracted] = useState(false);

  const navs: Nav[] = [
    {
      id: 'dashboard',
      icon: <House />,
      label: 'Painel SEMA',
    },
    {
      id: 'comparision',
      icon: <GitCompare />,
      label: 'Painel Comparativo',
    },
    {
      id: 'predictive',
      icon: <Brain />,
      label: 'Análise Preditiva',
    },
    {
      id: 'reports',
      icon: <FileText />,
      label: 'Relatórios',
    },
    {
      id: 'settings',
      icon: <Settings />,
      label: 'Configurações',
    },
  ];

  return (
    <div
      className={`bg-card border-r border-border flex flex-col transition-all duration-300 ease-in-out relative h-screen ${
        contracted ? 'w-fit' : 'w-64'
      }`}
    >
      <button
        data-tooltip-id="tooltip"
        data-tooltip-content={contracted ? 'Expandir Menu' : 'Recolher Menu'}
        data-tooltip-place="bottom"
        data-slot="tooltip-trigger"
        className="whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg:not([className*='size-'])]:size-4 shrink-0 [&amp;_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive hover:cursor-pointer hover:text-accent-foreground dark:hover:bg-accent/50 gap-1.5 has-[&gt;svg]:px-2.5 absolute -right-3 top-6 z-10 h-6 w-6 rounded-full border border-border bg-card shadow-md hover:bg-accent flex items-center justify-center p-0"
        data-state="closed"
        onClick={() => setContracted(!contracted)}
      >
        <Menu className="h-3 w-3" />
      </button>
      <div
        className={`border-b border-border transition-all duration-300 ${
          contracted ? 'p-3' : 'p-6'
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`bg-primary rounded-lg ${contracted ? 'p-1.5' : 'p-2'}`}
          >
            <TreePine
              className={`text-primary-foreground ${
                contracted ? 'h-5 w-5' : 'h-6 w-6'
              }`}
            />
          </div>
          {!contracted && (
            <div className="overflow-hidden">
              <h1 className="text-lg font-semibold whitespace-nowrap">
                SEMA-MT
              </h1>
              <p className="text-sm text-muted-foreground whitespace-nowrap">
                Painel de Diagnóstico
              </p>
            </div>
          )}
        </div>
      </div>
      <nav
        className={`flex-1 transition-all duration-300 ${
          contracted ? 'p-2' : 'p-4'
        }`}
      >
        <div className="space-y-2 flex flex-col">
          {navs.map((nav, index) => (
            <NavButton
              key={index}
              icon={nav.icon}
              selected={tabSelected === nav.id}
              label={nav.label}
              aria-describedby={`radix-:r${index + 1}:`}
              contracted={contracted}
              onClick={() => setTabSelected(nav.id)}
              {...(contracted && {
                'data-tooltip-id': 'tooltip',
                'data-tooltip-content': nav.label,
                'data-tooltip-place': 'top',
              })}
            />
          ))}
        </div>
      </nav>
      <div
        className={`border-t border-border transition-all duration-300  ${
          contracted ? 'p-2' : 'p-4'
        }`}
      >
        {!contracted && (
          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg mb-3">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
              <User className="h-4 w-4 text-primary-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">Admin SEMA</p>
              <p className="text-xs text-muted-foreground">Online</p>
            </div>
          </div>
        )}
        <div className={`flex gap-1.5 ${contracted ? 'flex-col' : 'flex-row'}`}>
          <button
            data-slot="sheet-trigger"
            className={`inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg:not([className*='size-'])]:size-4 [&amp;_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border-border text-foreground hover:cursor-pointer hover:bg-accent hover:text-accent-foreground dark:border-input dark:hover:bg-input/50 h-8 rounded-md gap-1.5 has-[&gt;svg]:px-2.5 p-2 relative flex-1 ${
              contracted
                ? 'border-none bg-transparent'
                : 'border bg-background dark:bg-input/30'
            }`}
            title="Notificações (3)"
            type="button"
            aria-haspopup="dialog"
            aria-expanded="false"
            aria-controls="radix-:r6:"
            data-state="closed"
          >
            <Bell className="h-4 w-4" />
            <div className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-[10px] text-white font-medium">3</span>
            </div>
          </button>
          <button
            data-tooltip-id="tooltip"
            data-tooltip-content={
              theme === 'dark' ? 'Modo Claro' : 'Modo Escuro'
            }
            data-tooltip-place="top"
            data-slot="tooltip-trigger"
            className={`inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg:not([className*='size-'])]:size-4 [&amp;_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border-border text-foreground hover:cursor-pointer hover:bg-accent hover:text-accent-foreground dark:border-input dark:hover:bg-input/50 h-8 rounded-md gap-1.5 has-[&gt;svg]:px-2.5 p-2 flex-1 ${
              contracted
                ? 'border-none bg-transparent'
                : 'border bg-background dark:bg-input/30'
            }`}
            data-state="closed"
            onClick={handleTheme}
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </button>
          <button
            data-tooltip-id="tooltip"
            data-tooltip-content="Sair"
            data-tooltip-place="top"
            data-slot="tooltip-trigger"
            className={`inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg:not([className*='size-'])]:size-4 [&amp;_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border-border dark:border-input hover:cursor-pointer dark:hover:bg-input/50 h-8 rounded-md gap-1.5 has-[&gt;svg]:px-2.5 p-2 text-destructive hover:text-destructive hover:bg-destructive/10 flex-1 ${
              contracted
                ? 'border-none bg-transparent'
                : 'border bg-background dark:bg-input/30'
            }`}
            data-state="closed"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
      {!contracted && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="w-5 h-5 bg-white rounded-sm flex items-center justify-center p-0.5 flex-shrink-0">
              <Image
                src={semaLogo}
                alt="Brasão do Estado de Mato Grosso"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="truncate">Secretaria do Meio Ambiente</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1 truncate">
            Governo do Estado de Mato Grosso
          </p>
        </div>
      )}
      <Tooltip
        id="tooltip"
        style={{
          backgroundColor: 'var(--primary)',
          borderRadius: '8px',
          padding: '6px 8px',
          fontSize: '12px',
        }}
      />
    </div>
  );
};

export default Sidebar;
